/**
 * Row shapes the estimator reads and writes. Column lists are explicit at
 * every call site; these interfaces document what each query returns.
 */

export const ESTIMATE_STATUSES = ['draft', 'sent', 'viewed', 'accepted', 'rejected', 'expired'] as const;
export type EstimateStatus = (typeof ESTIMATE_STATUSES)[number];

export function isEstimateStatus(value: string | null | undefined): value is EstimateStatus {
  return typeof value === 'string' && (ESTIMATE_STATUSES as readonly string[]).includes(value);
}

/** Mirrors the `solution_type` Postgres enum. */
export const SOLUTION_TYPES = [
  'managed_network',
  'video_surveillance',
  'access_control',
  'pro_av',
  'digital_signage',
  'security_alarm',
  'smart_robotics',
  'keytracer',
  'it_cloud',
  'pos_systems',
  'conferencing',
  'audio',
] as const;
export type SolutionType = (typeof SOLUTION_TYPES)[number];

export function isSolutionType(value: string | null | undefined): value is SolutionType {
  return typeof value === 'string' && (SOLUTION_TYPES as readonly string[]).includes(value);
}

export interface EstimateCustomerRef {
  id: string;
  name: string;
  account_number: string | null;
}

export interface EstimateLocationRef {
  id: string;
  name: string;
  address?: string | null;
}

export interface EstimateListRow {
  id: string;
  estimate_number: string;
  status: EstimateStatus | null;
  solution_type: SolutionType | null;
  contact_name: string | null;
  contact_email: string | null;
  total_cents: number | null;
  created_at: string;
  updated_at: string;
  proposal_sent_at: string | null;
  m365_last_reply_at: string | null;
  m365_message_id: string | null;
  followup_days: number | null;
  valid_until: string | null;
  customer: EstimateCustomerRef | null;
  location: EstimateLocationRef | null;
}

export const ESTIMATE_LIST_COLUMNS =
  'id, estimate_number, status, solution_type, contact_name, contact_email, total_cents, created_at, updated_at, proposal_sent_at, m365_last_reply_at, m365_message_id, followup_days, valid_until, customer:customers(id, name, account_number), location:locations(id, name)';

export interface EstimateDetailRow extends EstimateListRow {
  customer_id: string | null;
  location_id: string | null;
  contact_phone: string | null;
  cc_emails: string[] | null;
  scope_notes: string | null;
  hardware_margin_pct: number | null;
  labor_rate_per_hour: number | null;
  contingency_pct: number | null;
  hardware_subtotal_cents: number | null;
  labor_subtotal_cents: number | null;
  contingency_cents: number | null;
  total_labor_hours: number | null;
  followup_sent_at: string | null;
  m365_conversation_id: string | null;
}

export const ESTIMATE_DETAIL_COLUMNS =
  'id, estimate_number, status, solution_type, customer_id, location_id, contact_name, contact_email, contact_phone, cc_emails, scope_notes, hardware_margin_pct, labor_rate_per_hour, contingency_pct, hardware_subtotal_cents, labor_subtotal_cents, contingency_cents, total_cents, total_labor_hours, valid_until, followup_days, proposal_sent_at, followup_sent_at, m365_message_id, m365_conversation_id, m365_last_reply_at, created_at, updated_at, customer:customers(id, name, account_number), location:locations(id, name, address)';

export type LineItemType = 'hardware' | 'labor' | 'licensing' | 'recurring' | 'other';

export interface EstimateLineItemRow {
  id: string;
  estimate_id: string;
  type: LineItemType;
  description: string;
  quantity: number | null;
  unit_cost_cents: number | null;
  unit_sell_cents: number | null;
  sort_order: number | null;
}

export const LINE_ITEM_COLUMNS =
  'id, estimate_id, type, description, quantity, unit_cost_cents, unit_sell_cents, sort_order';

/** Values written on create/update. Money in cents; percentages as stored. */
export interface EstimateWriteValues {
  customer_id: string;
  location_id: string;
  contact_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  cc_emails: string[];
  solution_type: SolutionType;
  scope_notes: string | null;
  hardware_margin_pct: number;
  labor_rate_per_hour: number;
  contingency_pct: number;
  hardware_subtotal_cents: number;
  labor_subtotal_cents: number;
  contingency_cents: number;
  total_cents: number;
  total_labor_hours: number;
  valid_until: string | null;
  followup_days: number | null;
}

export interface LineItemWriteValues {
  type: 'hardware' | 'labor';
  description: string;
  quantity: number;
  unit_cost_cents: number;
  unit_sell_cents: number;
  sort_order: number;
}

export interface ProposalDocumentRow {
  id: string;
  title: string;
  s3_key: string;
  file_size_bytes: number | null;
  mime_type: string | null;
  category: string;
  location_id: string | null;
  created_at: string;
  updated_at: string;
}

export const PROPOSAL_DOCUMENT_COLUMNS =
  'id, title, s3_key, file_size_bytes, mime_type, category, location_id, created_at, updated_at';

/** Storage key `proposal-pdf-generate` writes for an estimate. */
export function proposalPdfKey(customerId: string, estimateNumber: string): string {
  return `proposals/${customerId}/${estimateNumber}.pdf`;
}
